import React, { useMemo, useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Paper,
  Checkbox,
  Chip,
  Button,
  Stack,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Close as CloseIcon,
  InvertColors as XrayIcon,
  Waves as UltrasoundIcon,
  Scanner as MRIIcon,
  DonutLarge as CTIcon,
  Science as ScienceIcon,
  OpenInNew as OpenIcon,
} from '@mui/icons-material';
import { useQuery } from 'react-query';
import { skipWhenOffline } from '../../offline';
import { keystoneService, HorseStudySummary } from '../../services/keystoneService';
import { AuthedKeystoneImage } from '../Keystone/AuthedKeystoneImage';
import { INSTANT_FORMATS } from '../../utils/dateUtils';
import { useFormatInstant } from '../../hooks/useClinicTimezone';

interface ImagingStudyPickerProps {
  open: boolean;
  horseId: string;
  admissionDateTime?: string | Date | null;
  dischargeDateTime?: string | Date | null;
  alreadyAttached?: string[]; // studyIds currently attached; shown disabled + checked
  onClose: () => void;
  onConfirm: (studies: HorseStudySummary[]) => void;
}

type WindowFilter = 'visit' | '30d' | '90d' | 'year' | 'all';

const modalityIcon = (name?: string | null) => {
  const n = (name || '').toLowerCase();
  if (n.includes('x-ray') || n.includes('ray') || n.includes('dx') || n.includes('rad')) return <XrayIcon />;
  if (n.includes('ultra') || n.includes('us')) return <UltrasoundIcon />;
  if (n.includes('mr')) return <MRIIcon />;
  if (n.includes('ct')) return <CTIcon />;
  return <ScienceIcon />;
};

const ImagingStudyPicker: React.FC<ImagingStudyPickerProps> = ({
  open,
  horseId,
  admissionDateTime,
  dischargeDateTime,
  alreadyAttached = [],
  onClose,
  onConfirm,
}) => {
  const [windowFilter, setWindowFilter] = useState<WindowFilter>('visit');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  // Keystone `studyDate` is an INSTANT (Asteris `StudyDateTime`, DATE-KINDS
  // §2.3) — rendered in the clinic's zone, matching AttachedImagingBlock and
  // the backend PDF. The window filters above compare epoch millis and are
  // zone-agnostic, so they stay as they are.
  const formatInstant = useFormatInstant();

  // Keystone-backed (online-only). The "Add studies" trigger is disabled
  // offline; this gate keeps a doomed fetch from firing if the dialog is
  // somehow open when connectivity drops.
  const { data: studies = [], isLoading, error } = useQuery<HorseStudySummary[]>(
    ['horse-studies', horseId, open],
    () => keystoneService.getStudiesForHorse(horseId, { limit: 100, thumbnails: 3 }),
    { enabled: open && !!horseId && skipWhenOffline() },
  );

  // Filter by the chosen window + pre-select "this visit" studies when the
  // dialog first opens (if user hasn't touched the checkboxes yet).
  const filtered = useMemo<HorseStudySummary[]>(() => {
    const admission = admissionDateTime ? new Date(admissionDateTime).getTime() : null;
    const discharge = dischargeDateTime ? new Date(dischargeDateTime).getTime() : Date.now();
    const inWindow = (d: string | null) => {
      if (!d) return false;
      const t = new Date(d).getTime();
      if (windowFilter === 'visit') {
        if (admission == null) return false;
        return t >= admission && t <= discharge;
      }
      if (windowFilter === 'all') return true;
      const daysAgo = windowFilter === '30d' ? 30 : windowFilter === '90d' ? 90 : 365;
      return Date.now() - t <= daysAgo * 86400000;
    };
    return studies.filter(s => inWindow(s.studyDate));
  }, [studies, windowFilter, admissionDateTime, dischargeDateTime]);

  // On first open of this horse's picker, preselect the "this visit" set.
  const [primed, setPrimed] = useState(false);
  useEffect(() => {
    if (!open) {
      setPrimed(false);
      setSelected(new Set());
      return;
    }
    if (primed || studies.length === 0) return;
    const initial = new Set<string>(alreadyAttached);
    if (admissionDateTime) {
      const admission = new Date(admissionDateTime).getTime();
      const discharge = dischargeDateTime ? new Date(dischargeDateTime).getTime() : Date.now();
      for (const s of studies) {
        if (!s.studyDate) continue;
        const t = new Date(s.studyDate).getTime();
        if (t >= admission && t <= discharge) initial.add(s.studyId);
      }
    }
    setSelected(initial);
    setPrimed(true);
  }, [open, studies, admissionDateTime, dischargeDateTime, alreadyAttached, primed]);

  const toggle = (studyId: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(studyId)) next.delete(studyId);
      else next.add(studyId);
      return next;
    });
  };

  const handleConfirm = () => {
    const chosen = studies.filter(s => selected.has(s.studyId));
    onConfirm(chosen);
  };

  const totalVisit = useMemo(() => {
    if (!admissionDateTime) return 0;
    const admission = new Date(admissionDateTime).getTime();
    const discharge = dischargeDateTime ? new Date(dischargeDateTime).getTime() : Date.now();
    return studies.filter(
      s => s.studyDate && new Date(s.studyDate).getTime() >= admission && new Date(s.studyDate).getTime() <= discharge,
    ).length;
  }, [studies, admissionDateTime, dischargeDateTime]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span>Attach Imaging to Discharge Summary</span>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {/* Filter pills */}
        <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
          {([
            { key: 'visit', label: `This visit${totalVisit ? ` (${totalVisit})` : ''}` },
            { key: '30d', label: 'Last 30 days' },
            { key: '90d', label: 'Last 90 days' },
            { key: 'year', label: 'Last year' },
            { key: 'all', label: `All (${studies.length})` },
          ] as { key: WindowFilter; label: string }[]).map(opt => (
            <Chip
              key={opt.key}
              label={opt.label}
              size="small"
              color={windowFilter === opt.key ? 'primary' : 'default'}
              variant={windowFilter === opt.key ? 'filled' : 'outlined'}
              onClick={() => setWindowFilter(opt.key)}
            />
          ))}
        </Box>

        {isLoading ? (
          <Box py={4} textAlign="center">
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">Failed to load imaging studies.</Alert>
        ) : filtered.length === 0 ? (
          <Box py={4} textAlign="center">
            <Typography color="text.secondary" variant="body2">
              {windowFilter === 'visit'
                ? 'No imaging studies during this admission. Try widening the window above.'
                : 'No imaging studies in this window.'}
            </Typography>
          </Box>
        ) : (
          <Stack spacing={1}>
            {filtered.map(s => {
              const isChecked = selected.has(s.studyId);
              const isAttached = alreadyAttached.includes(s.studyId);
              return (
                <Paper
                  key={s.studyId}
                  variant="outlined"
                  sx={{
                    p: 1.25,
                    cursor: isAttached ? 'default' : 'pointer',
                    opacity: isAttached ? 0.7 : 1,
                    borderColor: isChecked ? 'primary.main' : 'divider',
                    bgcolor: isChecked ? 'primary.50' : 'background.paper',
                  }}
                  onClick={() => {
                    if (!isAttached) toggle(s.studyId);
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1.5}>
                    <Checkbox
                      checked={isChecked}
                      disabled={isAttached}
                      onChange={() => toggle(s.studyId)}
                      onClick={e => e.stopPropagation()}
                    />
                    <Box sx={{ color: 'text.secondary' }}>{modalityIcon(s.modalityName)}</Box>

                    {/* Thumbnail strip */}
                    <Box display="flex" gap={0.5}>
                      {(s.thumbnails ?? []).slice(0, 3).map(t => (
                        <Box
                          key={t.attachmentId}
                          sx={{
                            width: 48,
                            height: 48,
                            overflow: 'hidden',
                            borderRadius: 0.5,
                            border: '1px solid',
                            borderColor: 'divider',
                          }}
                        >
                          <AuthedKeystoneImage
                            publicUrl={t.publicUrl}
                            alt=""
                            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                          />
                        </Box>
                      ))}
                      {(s.thumbnails ?? []).length === 0 && (
                        <Box
                          sx={{
                            width: 48,
                            height: 48,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: 0.5,
                            border: '1px dashed',
                            borderColor: 'divider',
                            color: 'text.disabled',
                            fontSize: 12,
                          }}
                        >
                          no thumb
                        </Box>
                      )}
                    </Box>

                    <Box flex={1} minWidth={180}>
                      <Typography variant="body2" fontWeight={500}>
                        {s.studyDate ? formatInstant(s.studyDate, INSTANT_FORMATS.date) : 'Unknown date'}
                        {s.modalityName ? ` · ${s.modalityName}` : ''}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Acc #{s.accessionNumber} · {s.numberOfImages ?? 0} images
                        {s.reportAvailable ? ' · Report' : ''}
                      </Typography>
                    </Box>

                    {s.viewerUrl && (
                      <Tooltip title="Preview in Keystone viewer">
                        <IconButton
                          size="small"
                          component="a"
                          href={s.viewerUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={e => e.stopPropagation()}
                        >
                          <OpenIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {isAttached && <Chip size="small" label="Attached" color="success" />}
                  </Box>
                </Paper>
              );
            })}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Typography variant="body2" color="text.secondary" sx={{ mr: 'auto', ml: 2 }}>
          {selected.size} selected
        </Typography>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleConfirm} disabled={selected.size === 0}>
          Attach {selected.size > 0 ? `${selected.size} stud${selected.size === 1 ? 'y' : 'ies'}` : ''}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImagingStudyPicker;
